import Vue from 'vue'
import Recordset from '@/providers/api/Recordset'
import context from '@/providers/context'
import i18n from '@/providers/i18n'
import {format as format_date} from 'date-fns'
import ItemImage from '@/helpers/components/display/ItemImage'
import _ from 'lodash';

export function dateformat(date, pattern = DATE_FORMAT) {
  if (pattern === 'datetime') {
    pattern = i18n.i18next.t('time_formats.datetime');
  }
  if (pattern === 'date') {
    pattern = i18n.i18next.t('time_formats.date');
  }
  if (typeof date === 'number') {
    date = new Date(date);
  } else {
    if (!Date.parse(date)) {
      return '-';
    } else {
      //regex for date: ****-**-** **:**:** or ****-**-**T**:**:** where * is a number
      var regex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}[\sT][0-9]{2}:[0-9]{2}:[0-9]{2}$/;
      if (typeof date === 'string' && regex.test(date)) {
        date = date.replace(' ', 'T') + '+0000';  //make it UTC
      }
    }
  }
  return format_date(date, pattern);
}

export function formatNumber(number, decimals = false) {
  var numeric;
  if (decimals) {
    var order = Math.pow(10, decimals);
    numeric = Number.parseFloat(Math.round(number * order) / order).toFixed(decimals);
  } else {
    numeric = Number.parseInt(number);
  }
  if (isNaN(numeric)) {
    return '0';
  }
  return numeric.toString();
}

export function dateFromApi(datestring) {
  if (typeof datestring !== 'string') {
    return datestring;
  }
  datestring = datestring.replace(' ', 'T') + '+0000';  //make it UTC
  return new Date(datestring);
}

export function date4api(date) {
  if (!Date.parse(date)) {
    return date;
  }
  if (!(date instanceof Date)) {
    date = new Date(Date.parse(date));
  }
  var epoch = Date.parse(date) + date.getTimezoneOffset() * 60000;
  return format_date(epoch, DATE_FORMAT);
}

// Transforms properties of date type so that they are [safedelay] seconds in the future (otherwise API rejects it)
export function ensureFutureStart(data, fields = ['start_at'], safedelay = 30) {
  if (
    !context.getters.api.lastResponse ||
    !context.getters.api.lastResponse.headers ||
    !Date.parse(context.getters.api.lastResponse.headers.date)
  ) {
    return data;
  }
  var APIdelayMs =
    //returned date from API in headers - user's local time at response
    Date.parse(context.getters.api.lastResponse.headers.date) -
    context.getters.api.lastResponse.userTime.getTime()
  ;
  if (isNaN(APIdelayMs)) {
    return data;
  }
  var addedDelayMs = APIdelayMs + safedelay * 1000;
  var minMs = new Date().getTime() + addedDelayMs;
  for (let field of fields) {
    if (!data.hasOwnProperty(field)) {
      continue;
    }
    let val = data[field];
    if (val.indexOf('+') === -1) {
      val = val.replace(' ', 'T') + '+0000';
    }
    if (
      data.hasOwnProperty(field) &&
      data[field] &&
      Date.parse(val) &&
      Date.parse(val) < minMs
    ) {
      //set to minimum time
      data[field] = date4api(new Date(minMs));
    }
  }
  return data;
}

function fields(Vue) {
  Vue.prototype.$fields = {
    expand (fields, defaults = {}) {
      var array = [];
      for (let [name, data] of Object.entries(fields)) {
        array.push(this.entry(data, name, defaults));
      }
      return array;
    },

    //get just label
    label (field) {
      if (typeof field === 'string') {
        return i18n.i18next.t(field);
      }
      var txt = null;
      if (typeof field === 'object') {
        if (field.hasOwnProperty('label')) {
          txt = field.label;
        } else if (field.hasOwnProperty('trans')) {
          txt = i18n.i18next.t(field.trans);
        }
        if (field.hasOwnProperty('help')) {
          txt += ' <abbr class="btn" title="' + i18n.i18next.t(field.help) + '"></abbr>';
        }
      }
      return txt;
    },

    //transform one field
    entry (field, name, defaults = {}) {
      //defaults = _.cloneDeep(defaults);
      //lodash clone or cloneDeep is not working for some reason??? WTF???

      //prepare default values
      defaults = _.merge({
        type: 'input',
      }, defaults);

      //init object and api
      var cell = {}
      , api = context.getters.api
      ;
      if (typeof field === 'object') {
        cell = Object.assign(cell, field);
      }
      cell.label = this.label(field);
      delete cell.help;
      //formatting
      if (!cell.hasOwnProperty('format') && !cell.hasOwnProperty('function')) {
        switch (cell.type) {
          case 'image':
          case 'imageselect':
          case 'imageupload':
            cell.format = 'image';
            cell.type = 'imageselect';
            break;
          case 'int':
          case 'integer':
            cell.format = 'int';
            cell.type = 'input';
            cell.inputType = 'number';
            break;
          case 'currency':
          case 'number':
          case 'float':
            cell.format = 'number';
            cell.type = 'input';
            cell.inputType = 'number';
            break;
          case 'email':
          case 'tel':
          case 'text':
          case 'url':
            cell.inputType = cell.type;
            cell.type = 'input';
            break;
          case 'select':
          case 'resource':
          case 'datetime':
          case 'eye':
          case 'visible':
          case 'visibility':
          case 'currency':
          case 'date':
          case 'html':
            cell.format = cell.type;
            break;
          default:
            cell.format = 'string';
            break;
        }
      }
      //transforming simple select options to the format needed by form generator
      if (cell.hasOwnProperty('values') && !Array.isArray(cell.values)) {
        cell.values = Object.entries(cell.values).map(([id, trans]) => ({
          id,
          name: i18n.i18next.t(trans),
        }));
      }

      //some basic renaming
      if (cell.type === 'textarea') {
        cell.type = 'textArea'; //plugin expects camelCase
      }
      if (cell.type === 'fancySelect') {
        cell.type = 'vueSelect';
      }
      if (cell.type === 'selectMulti') {
        cell.type = 'vueMultiSelect';
      }
      if (cell.type === 'eye' || cell.type === 'visible' || cell.type === 'visibility') {
        cell.type = 'boolean';
        cell.inputType = 'visibility';
      }
      if (cell.type === 'currency') {
        cell.type = 'number';
      }

      if (cell.type === 'resource' || cell.type === 'resourceMulti') {
        if (cell.type === 'resource') {
          cell.type = 'select';
        }
        if (cell.type === 'resourceMulti') {
          cell.type = 'vueMultiSelect';
        }
        cell.values = () => {
          var valuesFunction = cell.getValues;
          if (typeof valuesFunction !== 'function') {
            valuesFunction = (values) => {
              var options = [];
              for (let value of values) {
                options.push({
                  id: value.id,
                  name: value.hasOwnProperty('name') ? value.name : value.title,
                });
              }
              options.sort((a, b) => a.name.localeCompare(b.name))
              return options;
            };
          }
          var resurl = cell.resource;
          if (resurl.indexOf('?') === -1) {
            resurl += '?range=all';
          }
          return api.get(resurl).then(valuesFunction);
        }
      }

      var getProp = (item, name) => {
        var paths = name.split('.');
        var path = paths.shift();
        var i = item[path];
        if (typeof i !== 'object' || !paths.length) {
          return i;
        }
        return getProp(i, paths.join('.'));
      };

      //formatting function
      if (!cell.hasOwnProperty('function')) {
        switch (cell.format) {
          case 'resource':
            cell.function = item => {
              if (item[cell.resource]) {
                return item[cell.resource].name || item[cell.resource].title;
              }
              if (item[name]) {
                api.get(cell.resource + '/' + item[name] + '?range=single')
                  .then(res => {
                    Vue.set(field, 'format', 'string');
                    Vue.set(field, 'function', item => res.name || res.title);
                  })
                ;
              }
              return '';
            };
            break;
          case 'url':
            cell.function = (item, el) => el('a', {
              attrs: {
                href: getProp(item, name),
                target: '_blank',
              }
            }, [getProp(item, name)]);
            break;
          case 'date':
            cell.function = item => dateformat(getProp(item, name), 'DD.MM.YYYY');
            break;
          case 'datetime':
            cell.function = item => dateformat(getProp(item, name), 'DD.MM.YYYY HH:mm:ss');
            break;
          case 'money':
            cell.function = item => formatNumber(getProp(item, name), 2) + ' €';
            break;
          case 'price':
          case 'currency':
            cell.function = (item, el, c) => {
              var number = formatNumber(getProp(item, name), 2);
              if (typeof cell.currency === 'function') {
                return el('span', {}, [
                  number,
                  ' ',
                  cell.currency(item, el, c),
                ]);
              }
              return formatNumber(getProp(item, name), 2) +
                (cell.currency ? ' ' + cell.currency : '')
              ;
            };
            break;
          case 'tick':
            cell.function = (item, el) => el('Icon', {
              props: {name: getProp(item, name) ? 'tick' : 'close'},
              class: getProp(item, name) ? 'active' : 'inactive',
            });
            break;
          case 'eye':
          case 'visible':
          case 'visibility':
            cell.function = (item, el) => {
              var state = getProp(item, name) ? 'visible' : 'invisible';
              var trans = getProp(item, name) ? 'active' : 'inactive';
              return el('span', {
                style: {'white-space': 'nowrap'}
              }, [el('Icon', {
                props: {name: state},
                class: state,
              }), i18n.i18next.t(trans)]);
            };
            break;
          case 'int':
          case 'integer':
            cell.function = item => formatNumber(getProp(item, name), 0);
            break;
          case 'select':
            cell.function = item => {
              if (cell.values && Array.isArray(cell.values)) {
                var appropriate = cell.values.filter(v => v.id == getProp(item, name));
                if (appropriate.length) {
                  return appropriate[0].name;
                }
                return null;
              }
              return item[name];
            };
            break;
          case 'image':
            cell.function = (item, el) => {
              var i = item;
              if (name.indexOf('.') !== -1) {
                var path = name.split('.');
                path.pop();
                i = getProp(item, path.join('.'));
              }
              return el(ItemImage, {
                props: {
                  expandable: cell.expandable || false,
                  item: i,
                  size: cell.hasOwnProperty('size') ? cell.size : 1,
                },
              });
            };
            break;
          case 'html':
            cell.function = (item, el) => el('div', {
              domProps: {
                innerHTML: getProp(item, name)
              },
            });
            break;
          case 'string':
          default:
            cell.function = item => getProp(item, name);
            break;
        }
      }
      cell.name = name;
      cell.model = name;
      cell.styleClasses = 'field-' + name;
      cell = Object.assign({}, defaults, cell);
      if (cell.type === 'vueMultiSelect' || cell.type === 'vueSelect') {
        let multiple = cell.type === 'vueMultiSelect';
        cell.selectOptions = Object.assign(cell.selectOptions || {}, {
          multiple,
          searchable: true,
          closeOnSelect: !multiple,
        });
        if (typeof cell.values === 'object') {
          cell = this.setValues(cell, cell.values);
        }
        cell.type = 'vueMultiSelect';
      }
      return cell;
    },

    //get data from recordset
    model (recordset, fields) {
      if (!recordset.length) {
        throw new Error('Expecting at least one record');
      }
      var names = this.expand(fields).map(f => f.model);  //get just names
      return _.pick(recordset.list[0], names);  //keep just names
    },

    setValues (field, values) {
      if (Array.isArray(values)) {
        field.valuepool = {};
        for (let val of values) {
          field.valuepool[val.id] = val.name;
        }
        if (field.type === 'vueMultiSelect' || field.type === 'vueSelect') {
          field.selectOptions = Object.assign(field.selectOptions || {}, {
            searchable: true,
            closeOnSelect: field.type === 'vueSelect',
            customLabel: id => field.valuepool[id],
          });
          field.values = values.map(o => o.id);
        } else {
          field.values = values;
        }
      }
      return field;
    },
  };
}

export default fields;

Vue.use(fields);
if (fields.installed) {
  fields.installed = false;
}


// WEBPACK FOOTER //
// ./src/providers/fields.js